import { NumberFormatter } from "@internationalized/number"

import { UNKNOWN_PLATE_PREFIX } from "./etc"
import {
  COUNTRIES,
  getCountryCodesFromPhoneNumber,
  US_LOCALE,
} from "./localization"

export function normalizePhoneNumber(input: string) {
  const numbers = stripNonNumerics(input)

  switch (numbers.length) {
    case 0:
    case 1:
    case 2:
    case 3:
      return numbers // Just display as entered
    case 4:
    case 5:
    case 6:
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}` // Add a dash after first 3 digits
    case 7:
    case 8:
    case 9:
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}` // US format
    case 10:
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}` // Standard 10-digit format
    case 11:
      return `${numbers[0]} ${numbers.slice(1, 4)}-${numbers.slice(4, 7)}-${numbers.slice(7)}` // US-style with country code
    case 12:
    case 13:
    case 14:
      return `${numbers.slice(0, 2)} ${numbers.slice(2, 5)} ${numbers.slice(5, 8)} ${numbers.slice(8)}` // Simple international formatting
    default:
      return numbers.slice(0, 14) // No assumptions, just return as-is
  }
}

// DO NOT TOUCH - NOT IN E164
// Used in register/gated flow for displaying legacy phone numbers
// To accomododate legacy phone number inputs
export function removeUSCountryCode(input: string) {
  if (input.startsWith("+")) {
    const countryCode = getCountryCodesFromPhoneNumber(input)
    console.log("INTERNATIONAL", countryCode, input)
    if (countryCode.includes("US")) {
      const dialCode = COUNTRIES.get(countryCode[0])?.dialCode
      const phoneNumberWithoutDialCode = input.slice(dialCode?.length || 0)
      return phoneNumberWithoutDialCode
    }
  }

  return stripNonNumerics(input)
}

export const normalizeLongStringOfNumbers = (value: string) => {
  const groups = value.match(/\d{1,3}/g) || []
  if (groups.length > 1 && groups[groups.length - 1].length < 3) {
    const lastGroup = groups.pop()
    groups[groups.length - 1] += lastGroup
  }
  return groups.join("-")
}

export const stripNonNumerics = (value: string) => {
  return value.replace(/\D/g, "")
}

export const stripNonPhoneCharacters = (value: string) => {
  return value.replace(/[^\d+]/g, "")
}

export const prettyCurrencyNumber = (amount: number) => {
  const currencyFormatter = new NumberFormatter(US_LOCALE, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: !amount.toString().split(".")[1]?.length
      ? 0
      : undefined,
    maximumFractionDigits: 2,
  })

  return currencyFormatter.format(amount)
}

export const stripNonAlphaNumerics = (value: string) => {
  return value.replace(/[^a-z0-9]/gi, "")
}

// Normalizes what the user enters
export const normalizeLicensePlate = (value: string) => {
  return stripNonAlphaNumerics(value).toUpperCase()
}

// Access codes / membership keys: uppercase alphanumerics only (no spaces or special characters)
export const normalizeAccessCode = (value: string) => {
  return stripNonAlphaNumerics(value).toUpperCase()
}

// Normalizes what is returned by the api. Some of them are prefixed with "UNKNOWN"
export const normalizedAPILicensePlate = (plate?: string | null) => {
  if (!plate || plate.startsWith(UNKNOWN_PLATE_PREFIX)) {
    return "—"
  }
  return plate
}
